<template>
  <Combobox
    v-slot="{ open }"
    :model-value="currentOrganization"
    :default-value="selectedOrganizationDropdownItems[0]"
    aria-label="Change organization"
    @update:model-value="onSelectOrganization"
  >
    <div class="relative">
      <div
        class="mr-auto cursor-default overflow-hidden rounded-md tracking-wide transition-all duration-300"
        :class="{
          'w-full': open || mobile,
          'w-48': !open && !mobile,
          'float-right': !mobile,
        }"
      >
        <ComboboxButton as="div" class="relative cursor-pointer">
          <ComboboxInput
            class="cursor-pointer rounded-md border-none py-2 pl-3 leading-5 tracking-wide transition-all duration-150 focus:ring-0 focus:outline-none"
            :class="
              open
                ? 'w-full bg-white pr-8 text-gray-900'
                : `
                  w-full truncate bg-transparent pr-6 text-gray-200
                  lg:w-44 lg:text-right
                `
            "
            :display-value="
              (i) => {
                return (i as Organization | undefined)?.name ?? 'None';
              }
            "
            @change="query = $event.target.value"
            @focus="$event.target.select()"
          />
          <div
            class="absolute inset-y-0 right-0 flex items-center pr-2"
            :class="open ? 'text-gray-900' : 'text-gray-200'"
          >
            <Icon
              name="angle-down"
              class="size-4 transition-all duration-300"
              :class="{ 'rotate-180': open }"
            />
          </div>
        </ComboboxButton>
      </div>
      <Transition name="fade-100" @after-leave="query = ''">
        <div v-show="open">
          <ComboboxOptions
            static
            class="custom-table-scroll absolute top-10 z-30 max-h-screen-75 w-full overflow-auto rounded-md bg-white tracking-wide shadow-lg ring-1 ring-gray-400 focus:outline-none"
            @click="dataFiltersStore.resetSharableFilters"
          >
            <div
              v-if="
                selectedOrganizationDropdownItems.length === 0 &&
                query.length > 0
              "
              class="relative cursor-default px-4 py-2 text-gray-700 select-none"
            >
              No matches
            </div>
            <ComboboxOption
              v-for="organization in selectedOrganizationDropdownItems"
              v-else
              :key="organization.value"
              v-slot="{ selected, active }"
              :value="organization.value"
              as="template"
            >
              <li
                class="relative flex cursor-pointer items-center gap-2 p-2 pr-4 select-none"
                :class="{
                  'bg-burgundy-50 text-burgundy-900': active,
                  'text-gray-700': !active,
                }"
              >
                <span
                  v-if="organization.value === currentOrganization?.id"
                  class="flex items-center text-purple-900"
                >
                  <Icon name="check" class="size-4" />
                </span>
                <span v-else class="size-4" />
                <span
                  v-tooltip="organization.label"
                  class="block truncate"
                  :class="{ 'font-medium': selected, 'font-normal': !selected }"
                >
                  {{ organization.label }}
                </span>
                <span
                  v-if="organization.value === primaryOrganization"
                  class="rounded-lg px-2 text-xs text-purple-900 ring-1 ring-purple-800"
                >
                  Main
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Transition>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { useOrganizationsQuery } from "@/services/organization";
import {
  useProfileQuery,
  useUpdateSelectedOrganization,
} from "@/services/user";

import { useFuse } from "@/utils/arrays";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/vue";
import Icon from "@/components/Core/Icon.vue";
import { Organization } from "@/services/organization";
import { useDataFiltersStore } from "@/store/data_filters";
import { useTrack } from "@/services/gtm";

defineProps({
  mobile: {
    type: Boolean,
    default: false,
  },
});
const self = useProfileQuery();
const query = ref("");
const track = useTrack();

const primaryOrganization = computed(() => {
  return self.data?.organization;
});

const organizations = useOrganizationsQuery();

const filteredOrganizations = useFuse(
  computed(() => organizations.data ?? []),
  query,
  {
    keys: ["name"],
    threshold: 0.3,
  },
);

const currentOrganization = computed(() => {
  const currentOrg = self.data?.selectedOrganization ?? self.data?.organization;
  return organizations.data?.find(
    (organization) => organization.id === currentOrg,
  );
});
watch(currentOrganization, () => {
  dataFiltersStore.resetSharableFilters();
});
const selectedOrganizationDropdownItems = computed(() =>
  filteredOrganizations.value?.map((organization) => ({
    label: organization.name,
    value: organization.id,
  })),
);

const dataFiltersStore = useDataFiltersStore();
const updateSelectedOrganization = useUpdateSelectedOrganization();
function onSelectOrganization(orgId: number): void {
  if (orgId !== currentOrganization.value?.id) {
    track("switch_organization");
  }
  updateSelectedOrganization(orgId);
}
</script>
